import { useState, useCallback, useRef, useMemo, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { Loader2 } from '@digital-twins/design-system/icons'
import { Button } from '@/components/ui/button'
import { ToggleChip } from '@/components/ui/toggle-chip'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { taxonomyApi, usersApi } from '@/lib/api'
import { TaxonomyNodeDto } from '@/lib/api/client'
import { track, AnalyticsEvents } from '@/lib/services/analytics'
import { useTheme } from '@/lib/theme-provider'
import { OnboardingStepProps, TargetingType } from '@/types/components/onboarding'
import { FadeUp } from '../FadeUp'
import { getIndustryIcon } from '@/lib/constants/industry-categories'
import useSWR from 'swr'

function matchesSegment(segment: string | null, targetingType: TargetingType): boolean {
  if (targetingType === 'BOTH' || segment === null || segment === 'both') return true
  if (targetingType === 'B2B') return segment === 'b2b'
  if (targetingType === 'B2C') return segment === 'b2c'
  return true
}

function nodeHasChildren(node: TaxonomyNodeDto): boolean {
  return !!node.hasChildren
}

/**
 * IndustryStep — phased multi-select across tiers.
 *
 * Phase 0 (tier-1): pick one or more categories → Continue
 * Phase 1 (tier-2): children of selected tier-1 parents, grouped → pick → Continue
 * Phase 2 (tier-3): children of selected tier-2 parents, grouped → pick → Continue (saves)
 *
 * At every phase, leaf nodes are toggled into the final selection.
 * Parent nodes are toggled to determine which children appear in the next phase.
 */
export const IndustryStep = forwardRef<HTMLDivElement, OnboardingStepProps>(
  function IndustryStep({ wizardState, onUpdateState, onNext, onSkip, className }, ref) {
    const { t } = useTranslation(['onboarding', 'common'])
    const { resolvedTheme } = useTheme()
    const tone = resolvedTheme === 'dark' ? 'dark' : 'light'
    const { targetingType } = wizardState

    // Final leaf selections (sourceIds)
    const [selectedLeaves, setSelectedLeaves] = useState<Array<string>>([])

    // Parents toggled at each phase (tokens, not sourceIds)
    const [selectedParentsByPhase, setSelectedParentsByPhase] = useState<Array<Set<string>>>([
      new Set(), new Set(), new Set(),
    ])

    const [phase, setPhase] = useState(0)

    const [childrenMap, setChildrenMap] = useState<Record<string, Array<TaxonomyNodeDto>>>({})
    const childrenMapRef = useRef<Record<string, Array<TaxonomyNodeDto>>>({})
    const [isAdvancing, setIsAdvancing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const syncChildrenMap = (update: Record<string, Array<TaxonomyNodeDto>>) => {
      childrenMapRef.current = update
      setChildrenMap(update)
    }

    const { data: tier1Data, isLoading, error: tier1Error, mutate: retryTier1 } = useSWR<{ nodes: Array<TaxonomyNodeDto> }>(
      `industry-tier1-${targetingType}`,
      () => taxonomyApi.getNodes('industry', { tier: 1 }),
      { revalidateOnFocus: false, revalidateOnReconnect: false },
    )

    const tier1Nodes = (tier1Data?.nodes ?? []).filter((n) =>
      matchesSegment(n.segment, targetingType),
    )

    const fetchChildren = useCallback(async (parentToken: string): Promise<void> => {
      if (childrenMapRef.current[parentToken] !== undefined) return
      try {
        const { nodes } = await taxonomyApi.getNodes('industry', { parentToken })
        const updated = { ...childrenMapRef.current, [parentToken]: nodes }
        syncChildrenMap(updated)
      } catch {
        toast.warning(t('industry.subLoadFailed'))
      }
    }, [])

    // sourceId → name and token → name lookups
    const { sourceIdToName, tokenToName, tokenToSourceId } = useMemo(() => {
      const sidMap: Record<string, string> = {}
      const tokMap: Record<string, string> = {}
      const tokSidMap: Record<string, string> = {}
      for (const node of tier1Nodes) {
        if (node.sourceId) sidMap[node.sourceId] = node.name
        if (node.sourceId) tokSidMap[node.token] = node.sourceId
        tokMap[node.token] = node.name
      }
      for (const nodes of Object.values(childrenMap)) {
        for (const node of nodes) {
          if (node.sourceId) sidMap[node.sourceId] = node.name
          if (node.sourceId) tokSidMap[node.token] = node.sourceId
          tokMap[node.token] = node.name
        }
      }
      return { sourceIdToName: sidMap, tokenToName: tokMap, tokenToSourceId: tokSidMap }
    }, [tier1Nodes, childrenMap])

    // Nodes to display in the current phase
    const currentGroups: Array<{ parent: TaxonomyNodeDto | null; nodes: Array<TaxonomyNodeDto> }> = useMemo(() => {
      if (phase === 0) {
        return [{ parent: null, nodes: tier1Nodes }]
      }
      const prevPhaseParents = selectedParentsByPhase[phase - 1]
      const groups: Array<{ parent: TaxonomyNodeDto | null; nodes: Array<TaxonomyNodeDto> }> = []

      // Find all parent nodes from the previous phase that were selected
      const allPrevNodes = phase === 1
        ? tier1Nodes
        : Object.values(childrenMap).flat()

      for (const parentNode of allPrevNodes) {
        if (!prevPhaseParents.has(parentNode.token)) continue
        const children = childrenMap[parentNode.token] ?? []
        if (children.length > 0) {
          groups.push({ parent: parentNode, nodes: children })
        }
      }
      return groups
    }, [phase, tier1Nodes, selectedParentsByPhase, childrenMap])

    const allCurrentNodes = useMemo(
      () => currentGroups.flatMap((g) => g.nodes),
      [currentGroups],
    )

    // How many parent nodes (with children) are toggled in this phase
    const selectedParentsThisPhase = selectedParentsByPhase[phase]
    const hasParentSelections = selectedParentsThisPhase.size > 0
    // Are there any leaf selections or parent selections at this phase?
    const hasAnySelectionThisPhase = hasParentSelections || allCurrentNodes.some(
      (n) => !nodeHasChildren(n) && !!n.sourceId && selectedLeaves.includes(n.sourceId),
    )

    function toggleParent(token: string) {
      setSelectedParentsByPhase((prev) => {
        const updated = [...prev]
        const set = new Set(updated[phase])
        if (set.has(token)) {
          set.delete(token)
        } else {
          set.add(token)
        }
        updated[phase] = set
        return updated
      })
    }

    function toggleLeaf(sourceId: string) {
      setSelectedLeaves((prev) =>
        prev.includes(sourceId) ? prev.filter((s) => s !== sourceId) : [...prev, sourceId],
      )
    }

    function removeLeafSelection(sourceId: string) {
      setSelectedLeaves((prev) => prev.filter((s) => s !== sourceId))
    }

    function removeParentSelection(token: string) {
      setSelectedParentsByPhase((prev) => {
        const updated = [...prev]
        for (let i = 0; i < updated.length; i++) {
          const set = new Set(updated[i])
          if (set.has(token)) {
            set.delete(token)
            updated[i] = set
          }
        }
        return updated
      })
    }

    async function handleAdvance() {
      if (!hasAnySelectionThisPhase && phase === 0) {
        // Nothing selected at phase 0 — treat as skip (no API call)
        onUpdateState({ selectedIndustries: [] })
        onSkip()
        return
      }

      if (!hasParentSelections || phase >= 1) {
        // No parents with children, or already at the deepest phase we show — save
        await handleSave()
        return
      }

      // Fetch children for all selected parents in this phase
      setIsAdvancing(true)
      const parentTokens = Array.from(selectedParentsThisPhase)
      await Promise.all(parentTokens.map((t) => fetchChildren(t)))
      setIsAdvancing(false)

      // Check if any of the fetched children are actually available
      const hasNextTierNodes = parentTokens.some((t) => {
        const children = childrenMapRef.current[t]
        return children && children.length > 0
      })

      if (hasNextTierNodes) {
        setPhase((p) => p + 1)
      } else {
        await handleSave()
      }
    }

    async function handleSave() {
      setIsSaving(true)
      const selectedAncestorSourceIds = selectedParentsByPhase
        .flatMap((set) => Array.from(set))
        .map((token) => tokenToSourceId[token])
        .filter((sourceId): sourceId is string => Boolean(sourceId))
      try {
        await usersApi.updateTargetingPreferences({
          targetingPreferences: {
            industryTokens: selectedLeaves,
            industryAncestorSourceIds: selectedAncestorSourceIds,
          },
        })
        track(AnalyticsEvents.ONBOARDING_INDUSTRY_SELECTED, {
          selected_count: selectedLeaves.length,
          tokens: selectedLeaves,
        })
        onUpdateState({
          selectedIndustries: selectedLeaves,
          selectedIndustryAncestors: selectedAncestorSourceIds,
        })
        onNext()
      } catch {
        toast.warning(t('industry.saveWarning'))
        onUpdateState({
          selectedIndustries: selectedLeaves,
          selectedIndustryAncestors: selectedAncestorSourceIds,
        })
        onNext()
      } finally {
        setIsSaving(false)
      }
    }

    const phaseLabels = [t('industry.phase0'), t('industry.phase1')]

    return (
      <div ref={ref} tabIndex={-1} className={cn('flex flex-col gap-6 outline-none', className)}>
        {/* Heading */}
        <FadeUp delay={0} className="text-center">
          <Heading level={1} size="4xl">
            {t('industry.title')}
          </Heading>
          <Text size="sm" color="muted" className="mt-2">
            {phaseLabels[phase] ?? t('industry.selectAll')}
          </Text>
        </FadeUp>

        {/* Chip grid */}
        <FadeUp delay={60}>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : tier1Error ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Text size="sm" color="muted">{t('industry.loadFailed')}</Text>
              <Button variant="outline" size="sm" onClick={() => retryTier1()}>{t('retry', { ns: 'common' })}</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-5" role="group" aria-label={t('industry.aria')}>
              {currentGroups.map((group, gi) => (
                <div key={group.parent?.token ?? 'root'} className="flex flex-col gap-2">
                  {group.parent && (
                    <div className="flex items-center justify-center gap-1.5">
                      {(() => {
                        const tier1Name = group.parent.fullPath?.split(' | ')[0] ?? group.parent.name
                        const ParentIcon = getIndustryIcon(tier1Name)
                        return <ParentIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      })()}
                      <Text size="xs" color="muted" className="font-medium">
                        {group.parent.name}
                      </Text>
                    </div>
                  )}
                  <div className="flex flex-wrap justify-center gap-2">
                    {group.nodes.map((node) => {
                      const hasChildren = nodeHasChildren(node)
                      const isLeaf = !hasChildren
                      const isSelectedLeaf = isLeaf && !!node.sourceId && selectedLeaves.includes(node.sourceId)
                      const isSelectedParent = hasChildren && selectedParentsThisPhase.has(node.token)
                      const Icon = phase === 0 ? getIndustryIcon(node.name) : null

                      return (
                        <ToggleChip
                          key={node.token}
                          variant="platform"
                          tone={tone}
                          selected={isSelectedLeaf || isSelectedParent}
                          showCheck
                          data-testid={`industry-chip-${node.token}`}
                          onClick={() => hasChildren ? toggleParent(node.token) : toggleLeaf(node.sourceId!)}
                        >
                          {Icon && <Icon className="mr-1.5 h-3.5 w-3.5 shrink-0" />}
                          {node.name}
                        </ToggleChip>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </FadeUp>

        {/* Actions */}
        <FadeUp delay={200} className="flex flex-col gap-2">
          <Button
            data-testid="industry-continue-button"
            variant="cta"
            onClick={handleAdvance}
            disabled={(phase > 0 && !hasAnySelectionThisPhase) || isSaving || isAdvancing}
            className="w-full"
          >
            {(isSaving || isAdvancing) ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isSaving ? t('saving', { ns: 'common' }) : t('loading', { ns: 'common' })}
              </>
            ) : (
              t('continue', { ns: 'common' })
            )}
          </Button>

          {/* "You can change this later" skip link */}
          <button
            type="button"
            data-testid="industry-skip-link"
            onClick={onSkip}
            className="text-center text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {t('industry.skipLater')}
          </button>
        </FadeUp>
      </div>
    )
  },
)
